import { useRef, useState } from "react";
import { api } from "../../../data";
import { useAction, useRueckfrage } from "../../../components/ui";
import { StoredImage } from "../../creator/ui";
import { t } from "../../../lib/sprache";
import { addKitAsset, KIT_FOTOS_MAX, KIT_LOGOS_MAX, removeKitAsset, updateKitAsset, type KitAssetRow } from "./mediakit-daten";

/**
 * Bilder des Media-Kits (0214): Fotos für die Collage (höchstens 7) und Marken-Logos für „Trusted by“ (höchstens 24).
 * Hochladen, Reihenfolge ändern, entfernen; Logos bekommen den Markennamen. Bilder werden vor dem Hochladen verkleinert,
 * Fotos verlieren dabei Aufnahmeort und Gerätedaten.
 */
export function KitBilder({ orgId, talentId, assets, reload, gesperrt }: { orgId: string; talentId: string; assets: KitAssetRow[]; reload: () => void; gesperrt: boolean }) {
  return (
    <>
      <Gruppe orgId={orgId} talentId={talentId} kind="photo" liste={assets.filter((a) => a.kind === "photo")} reload={reload} gesperrt={gesperrt} />
      <Gruppe orgId={orgId} talentId={talentId} kind="logo" liste={assets.filter((a) => a.kind === "logo")} reload={reload} gesperrt={gesperrt} />
    </>
  );
}

function Gruppe({ orgId, talentId, kind, liste, reload, gesperrt }: { orgId: string; talentId: string; kind: "photo" | "logo"; liste: KitAssetRow[]; reload: () => void; gesperrt: boolean }) {
  const { busy, error, run } = useAction();
  const rueckfrage = useRueckfrage();
  const eingabe = useRef<HTMLInputElement>(null);
  const [namen, setNamen] = useState<Record<string, string>>(() => Object.fromEntries(liste.map((a) => [a.id, a.label ?? ""])));
  const [stand, setStand] = useState<string | null>(null);
  const [einfarbig, setEinfarbig] = useState(true);
  const foto = kind === "photo";
  const max = foto ? KIT_FOTOS_MAX : KIT_LOGOS_MAX;
  const frei = Math.max(0, max - liste.length);
  const id = foto ? "kit-fotos" : "kit-logos";

  function hochladen(files: FileList | null) {
    const wahl = [...(files ?? [])].slice(0, frei);
    if (!wahl.length) return;
    void run("hoch", async () => {
      let pos = liste.reduce((m, a) => Math.max(m, a.position), -1) + 1;
      for (const [i, f] of wahl.entries()) {
        setStand(t(`Bild ${i + 1} von ${wahl.length} wird hochgeladen …`, `Uploading image ${i + 1} of ${wahl.length} …`));
        // Markenname aus dem Dateinamen als Vorschlag (ohne Endung, Trenner als Leerzeichen)
        const name = foto ? null : (f.name.replace(/\.[a-z0-9]+$/i, "").replace(/[-_]+/g, " ").replace(/\b(logo|transparent|black|schwarz|png|svg)\b/gi, "").replace(/\s+/g, " ").trim() || null);
        await addKitAsset(api, orgId, talentId, kind, f, name, pos++, einfarbig);
      }
      setStand(null);
      if (eingabe.current) eingabe.current.value = "";
      reload();
    }, foto ? t("Fotos gespeichert.", "Photos saved.") : t("Logos gespeichert.", "Logos saved.")).finally(() => setStand(null));
  }
  const tauschen = (i: number, j: number) => {
    const a = liste[i], b = liste[j];
    if (!a || !b) return;
    void run(`pos-${a.id}`, async () => {
      // Positionen neu von 0 an vergeben, damit gleiche Positionen (alte Daten) die Reihenfolge nicht blockieren
      const neu = liste.map((x) => x.id);
      [neu[i], neu[j]] = [neu[j]!, neu[i]!];
      await Promise.all(neu.map((aid, p) => (liste.find((x) => x.id === aid)!.position === p ? Promise.resolve() : updateKitAsset(api, aid, { position: p }))));
      reload();
    });
  };
  const entfernen = async (a: KitAssetRow) => {
    if (!(await rueckfrage.ask({ title: foto ? t("Foto entfernen?", "Remove photo?") : t("Logo entfernen?", "Remove logo?"), text: t("Es verschwindet aus dem Media-Kit und aus allen Links.", "It disappears from the media kit and from all links."), confirmLabel: t("Entfernen", "Remove"), danger: true }))) return;
    void run(`weg-${a.id}`, async () => { await removeKitAsset(api, a.id); reload(); }, t("Entfernt.", "Removed."));
  };

  return (
    <section className="panel stack" aria-labelledby={id}>
      {rueckfrage.element}
      <h2 id={id}>{foto ? t("Fotos für die Collage", "Photos for the collage") : t("Marken-Logos („Trusted by“)", "Brand logos (“Trusted by”)")}</h2>
      <p className="source-note">
        {foto
          ? t(`Bis zu ${KIT_FOTOS_MAX} Fotos. Das erste und das letzte stehen ab fünf Fotos als Hochformat links und rechts, die anderen dazwischen. Ohne Fotos zeigt das Kit das Profilbild.`,
              `Up to ${KIT_FOTOS_MAX} photos. From five photos on, the first and last appear as portrait on the left and right, the others in between. Without photos the kit shows the profile picture.`)
          : t(`Bis zu ${KIT_LOGOS_MAX} Logos, am besten PNG oder SVG mit durchsichtigem Hintergrund. Sie erscheinen gleich groß und einfarbig in Reihen, die letzte Reihe mittig. Ohne Logos entfällt der Abschnitt oder nennt die freigegebenen Referenzen als Text. Nur Marken zeigen, deren Nennung vereinbart ist.`,
              `Up to ${KIT_LOGOS_MAX} logos, ideally PNG or SVG with a transparent background. They appear equally sized and monochrome in rows, the last row centred. Without logos the section is omitted or lists the released references as text. Only show brands whose mention has been agreed.`)}
      </p>
      {liste.length > 0 && (
        <ul className={`tk-kitbilder${foto ? "" : " logos"}`}>
          {liste.map((a, i) => (
            <li key={a.id}>
              <div className="tk-kitbild"><StoredImage path={a.path} alt={a.label ?? (foto ? t(`Foto ${i + 1}`, `Photo ${i + 1}`) : t(`Logo ${i + 1}`, `Logo ${i + 1}`))} /></div>
              {foto && (
                <label className="sr-only-label">{t("Ausschnitt", "Crop")}
                  <select value={a.label ?? "mitte"} disabled={gesperrt || !!busy}
                    onChange={(e) => { const v = e.target.value; void run(`aus-${a.id}`, async () => { await updateKitAsset(api, a.id, { label: v === "mitte" ? null : v }); reload(); }); }}>
                    <option value="mitte">{t("Mitte", "Centre")}</option>
                    <option value="oben">{t("Oben", "Top")}</option>
                    <option value="unten">{t("Unten", "Bottom")}</option>
                    <option value="links">{t("Links", "Left")}</option>
                    <option value="rechts">{t("Rechts", "Right")}</option>
                  </select>
                </label>
              )}
              {!foto && (
                <label className="sr-only-label">{t("Marke", "Brand")}
                  <input maxLength={80} value={namen[a.id] ?? a.label ?? ""} disabled={gesperrt} placeholder={t("Name der Marke", "Brand name")}
                    onChange={(e) => setNamen((n) => ({ ...n, [a.id]: e.target.value }))}
                    onBlur={() => { const v = (namen[a.id] ?? "").trim(); if (v !== (a.label ?? "")) void run(`name-${a.id}`, async () => { await updateKitAsset(api, a.id, { label: v || null }); reload(); }); }} />
                </label>
              )}
              <div className="row tk-kitbild-knoepfe">
                <button type="button" disabled={gesperrt || !!busy || i === 0} onClick={() => tauschen(i, i - 1)} aria-label={t(`Bild ${i + 1} nach vorn`, `Move image ${i + 1} forward`)}>←</button>
                <button type="button" disabled={gesperrt || !!busy || i === liste.length - 1} onClick={() => tauschen(i, i + 1)} aria-label={t(`Bild ${i + 1} nach hinten`, `Move image ${i + 1} back`)}>→</button>
                <button type="button" disabled={gesperrt || !!busy} onClick={() => void entfernen(a)}>{t("Entfernen", "Remove")}</button>
              </div>
            </li>
          ))}
        </ul>
      )}
      {!foto && (
        <label className="row" style={{ gap: 8, alignItems: "flex-start" }}>
          <input type="checkbox" checked={einfarbig} onChange={(e) => setEinfarbig(e.target.checked)} />
          <span>{t("Beim Hochladen einfarbig dunkel umwandeln (empfohlen)", "Convert to a single dark colour on upload (recommended)")}<br />
            <small>{t("Alle Logos erscheinen dann einheitlich dunkel auf hellem Grund, auch weiße oder bunte. Ein einfarbiger Hintergrund wird entfernt, der leere Rand abgeschnitten.", "All logos then appear uniformly dark on a light background, including white or coloured ones. A plain background is removed and the empty margin trimmed.")}</small></span>
        </label>
      )}
      {(stand ?? error) && <p className={error && !stand ? "tk-error" : undefined} role={error && !stand ? "alert" : "status"}>{stand ?? error}</p>}
      <div className="row">
        <label className="btn">
          {frei === 0 ? t(`Höchstzahl erreicht (${max})`, `Maximum reached (${max})`) : foto ? t("Fotos hochladen", "Upload photos") : t("Logos hochladen", "Upload logos")}
          <input ref={eingabe} type="file" className="sr-only" accept={foto ? "image/png,image/jpeg,image/webp" : "image/png,image/jpeg,image/webp,image/svg+xml"} multiple disabled={gesperrt || !!busy || frei === 0} onChange={(e) => hochladen(e.target.files)} />
        </label>
        <small>{t(`${liste.length} von ${max}`, `${liste.length} of ${max}`)}</small>
      </div>
    </section>
  );
}
